import type { BuildEvent } from './api/types.js';

function field(value: unknown, key: string): unknown {
  if (typeof value !== 'object' || value === null) return undefined;
  return (value as Record<string, unknown>)[key];
}

export interface EventSummary {
  title: string;
  detail?: string;
  /** Coarse kind, so the timeline can tint the row. */
  kind: 'message' | 'tool' | 'status' | 'meta';
}

/** Turns a raw build event into a human-readable transcript line. */
export function describeEvent(event: BuildEvent): EventSummary {
  const p = event.payload;
  switch (event.type) {
    case 'agent.message': {
      const content = field(p, 'content');
      const text = Array.isArray(content)
        ? content
            .map((block) => field(block, 'text'))
            .filter((t): t is string => typeof t === 'string')
            .join(' ')
        : '';
      return { title: 'Assistant', detail: text || undefined, kind: 'message' };
    }
    case 'agent.tool_use': {
      const name = field(p, 'name');
      const input = field(p, 'input');
      const command = field(input, 'command');
      return {
        title: `Tool · ${typeof name === 'string' ? name : 'unknown'}`,
        detail:
          typeof command === 'string'
            ? command
            : input !== undefined
              ? JSON.stringify(input)
              : undefined,
        kind: 'tool',
      };
    }
    case 'agent.tool_result': {
      const content = field(p, 'content');
      const text =
        typeof content === 'string'
          ? content
          : Array.isArray(content)
            ? content
                .map((block) => field(block, 'text'))
                .filter((t): t is string => typeof t === 'string')
                .join(' ')
            : undefined;
      return { title: 'Tool result', detail: text?.slice(0, 2000), kind: 'tool' };
    }
    case 'session.status_running':
      return { title: 'Session running', kind: 'status' };
    case 'session.status_idle': {
      const reason = field(field(p, 'stop_reason'), 'type');
      const detail = typeof reason === 'string' ? reason : undefined;
      return { title: `Session idle${detail ? ` · ${detail}` : ''}`, kind: 'status' };
    }
    case 'session.status_terminated':
      return { title: 'Session terminated', kind: 'status' };
    case 'span.model_request_end':
      return { title: 'Model request', kind: 'meta' };
    default:
      return { title: event.type, kind: 'meta' };
  }
}

/** Best-effort one-line description of a tool the agent wants to run. */
export function describeToolInput(toolName: string, input: unknown): string {
  const command = field(input, 'command');
  if (typeof command === 'string') return command;
  return input === undefined ? toolName : JSON.stringify(input);
}

/** Renders the event stream to a plain-text transcript. */
export function eventsToTranscript(events: BuildEvent[]): string {
  return events
    .map((event) => {
      const summary = describeEvent(event);
      return summary.detail ? `[${summary.title}]\n${summary.detail}` : `[${summary.title}]`;
    })
    .join('\n\n');
}
